import React, { useEffect, useState } from "react";
import StaticPopup from "components/StaticPopup";
import { getSupplierTrustedCompanies } from "services/supplierTrustService";

export const VerifiedAssociationIcon = ({ className = "h-5 w-5" }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${className}`}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8 12 2.5 2.5L16 9" />
  </svg>
);

const TrustedCompaniesSection = ({ supplierId, entityId, active, associationKey, onVerificationChange }) => {
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ status: "loading", page: 1, data: null });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  useEffect(() => {
    setSelectedCategory(null);
    if (!active) return undefined;
    const controller = new AbortController();
    let current = true;
    setState({ status: "loading", page, data: null });

    getSupplierTrustedCompanies({ supplierId, entityId, page, signal: controller.signal })
      .then((data) => {
        if (!current) return;
        setState({ status: "ready", page, data });
        onVerificationChange?.({ key: associationKey, verified: data.pagination.total > 0 });
      })
      .catch((error) => {
        if (!current || controller.signal.aborted) return;
        setState({ status: error.response?.status === 403 ? "forbidden" : "error", page, data: null });
        onVerificationChange?.({ key: associationKey, verified: false });
      });

    return () => {
      current = false;
      controller.abort();
    };
  }, [supplierId, entityId, page, retry, active, associationKey, onVerificationChange]);

  if (state.status === "forbidden") return null;

  const loading = state.status === "loading" || state.page !== page;
  const pagination = state.data?.pagination;
  const companies = state.data?.items || [];

  return (
    <section aria-labelledby="trusted-companies-heading" className="rounded-2xl border border-[#E8E6DD] bg-white/90 p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded((value) => !value)}
        aria-expanded={isExpanded}
        aria-controls="trusted-companies-content"
        className={`flex w-full items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-[#FFF0E4] via-[#FFF8F1] to-[#F3F6FF] px-3 py-3 text-left ${isExpanded ? "mb-4" : ""}`}
      >
        <div className="flex flex-col">
        <h3 id="trusted-companies-heading" className="text-sm font-semibold text-[#37221f]">
          Verified Business Associations
        </h3>
        <p className="mt-1 text-xs leading-5 text-[#667085]">This information is visible only to your business and cannot be viewed by any other vendor</p>
        </div>
        <span className="flex shrink-0 items-center gap-2">
          {!loading && pagination?.total > 0 && (
            <span className="rounded-full bg-[#EEF4FF] px-2.5 py-1 text-[10px] font-semibold text-[#185FA5]">
              {pagination.total} {pagination.total === 1 ? "company" : "companies"}
            </span>
          )}
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20"
            fill="none"
            className={`h-4 w-4 text-[#667085] transition-transform ${isExpanded ? "rotate-180" : ""}`}
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isExpanded && (
        <div id="trusted-companies-content">
          {loading ? (
        <div role="status" aria-live="polite">
          <span className="sr-only">Loading business associations...</span>
          <div aria-hidden="true" className="space-y-3 motion-safe:animate-pulse">
            {[0, 1].map((item) => (
              <div key={item} className="space-y-3 rounded-xl border border-[#EFEFE8] bg-[#FCFCFA] p-3">
                <div className="h-3 w-2/3 rounded bg-[#E8EDF5]" />
                <div className="h-3 w-1/2 rounded bg-[#EFEFE8]" />
                <div className="h-3 w-5/6 rounded bg-[#EFEFE8]" />
              </div>
            ))}
          </div>
        </div>
      ) : state.status === "error" ? (
        <div role="alert" className="rounded-xl border border-[#F4D3BA] bg-[#FFF8F1] p-3 text-xs leading-5 text-[#9A4B13]">
          <p>Unable to load business associations. The rest of the supplier profile is still available.</p>
          <button type="button" onClick={() => setRetry((value) => value + 1)} className="mt-2 font-semibold underline">Retry</button>
        </div>
      ) : !companies.length ? (
        <p className="rounded-xl border border-dashed border-[#E8E6DD] bg-[#FCFCFA] px-3 py-4 text-xs text-[#667085]">
          No verified business associations found.
        </p>
      ) : (
        <div className="divide-y divide-[#E8E6DD] border-t border-[#E8E6DD]">
          {companies.map((company) => (
            <article key={company.entityId} className="py-5 last:pb-0">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3F6FB] text-[#52647C]">
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                    <path d="M3 21h18M6 21V5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v16M15 9h3a2 2 0 0 1 2 2v10M10 17v4" />
                  </svg>
                </span>
                <div className="min-w-0 flex-1">
                  <h4 className="break-words text-sm font-semibold leading-5 text-[#1F2937]">{company.entityName}</h4>
                  <p className="mt-1 flex items-center gap-1 text-xs font-medium leading-5 text-[#079455]">
                    <VerifiedAssociationIcon className="h-3.5 w-3.5" />
                    Company Verified
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
                {(company.categories || []).map((category) => (
                  <button
                    key={category.key}
                    type="button"
                    aria-haspopup="dialog"
                    onClick={() => setSelectedCategory({ companyName: company.entityName, category })}
                    className="flex w-full min-w-0 items-start justify-between gap-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 text-left transition hover:border-[#FD6600] hover:bg-[#F1F5F9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD6600] focus-visible:ring-offset-2"
                  >
                    <span className="min-w-0 flex-1 break-words text-sm font-semibold leading-5 text-[#1F2937]">{category.name}</span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span title="Distinct awarded RFQs. A multi-project RFQ can appear under more than one location." className="rounded-full border border-white bg-white/90 px-2 py-0.5 text-[10px] font-medium leading-4 text-[#9A4B13]">
                        {category.rfqCount} {category.rfqCount === 1 ? "RFQ" : "RFQs"} awarded
                      </span>
                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-[#9A4B13]">
                        <path d="m9 6 6 6-6 6" />
                      </svg>
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] leading-5 text-[#667085]">Commercial details are confidential.</p>
            </article>
          ))}
        </div>
      )}

          {!loading && pagination?.totalPages > 1 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-[#EFEFE8] pt-3">
          <p className="text-[11px] text-[#667085]">
            Page {page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <button type="button" disabled={page === 1} onClick={() => setPage((value) => value - 1)} className="rounded-full border border-[#BFD2F5] px-3 py-1 text-xs font-semibold text-[#185FA5] disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
            <button type="button" disabled={page >= pagination.totalPages} onClick={() => setPage((value) => value + 1)} className="rounded-full border border-[#BFD2F5] px-3 py-1 text-xs font-semibold text-[#185FA5] disabled:cursor-not-allowed disabled:opacity-40">Next</button>
          </div>
        </div>
          )}
        </div>
      )}

      <div onClick={(event) => event.stopPropagation()}>
        <StaticPopup
          openModal={active && !loading && Boolean(selectedCategory)}
          handleClose={() => setSelectedCategory(null)}
          header={selectedCategory?.category.name || "Category details"}
          width="3xl"
          zIndex={10100}
          bodyStyle="px-4 py-4 sm:px-6"
        >
          {selectedCategory && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-semibold text-[#1F2937]">{selectedCategory.companyName}</p>
                  <p className="mt-1 flex items-center gap-1 text-xs font-medium text-[#079455]">
                    <VerifiedAssociationIcon className="h-3.5 w-3.5" />
                    Company Verified
                  </p>
                </div>
                <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-[#9A4B13]">
                  {selectedCategory.category.rfqCount} {selectedCategory.category.rfqCount === 1 ? "RFQ" : "RFQs"} awarded
                </span>
              </div>
              {selectedCategory.category.locations.length ? (
                <ul className="max-h-[55vh] divide-y divide-[#F0F2F5] overflow-y-auto overscroll-contain rounded-xl border border-[#E8E6DD] px-4">
                  {selectedCategory.category.locations.map((location) => (
                    <li key={location.key} className="flex items-start gap-3 py-4">
                      <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 h-4 w-4 shrink-0 text-[#667085]">
                        <path d="M20 10c0 6-8 11-8 11S4 16 4 10a8 8 0 1 1 16 0Z" />
                        <circle cx="12" cy="10" r="2.5" />
                      </svg>
                      <div className="min-w-0 flex-1">
                        <p className="break-words text-sm font-semibold leading-5 text-[#344054]">
                          {[location.areas.join(", "), location.city, location.states.join(", ")].filter(Boolean).join(" \u2192 ")}
                        </p>
                        <p className="mt-1 text-xs leading-5 text-[#667085]">
                          {location.financialYears.join(", ") || "Financial year not recorded"}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-[#EEF4FF] px-2 py-1 text-xs font-medium text-[#185FA5]">
                        {location.rfqCount} {location.rfqCount === 1 ? "RFQ" : "RFQs"}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-[#667085]">No location details recorded.</p>
              )}
              <p className="text-xs text-[#667085]">Commercial details are confidential.</p>
            </div>
          )}
        </StaticPopup>
      </div>
    </section>
  );
};

export default TrustedCompaniesSection;
